import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Contract,
  ContractStage,
  Contractor,
  Employee,
  PlanfactOperation,
  SmoUlClosingStatus,
  Task,
  getContractStages,
  getContractors,
  getContracts,
  getEmployees,
  getPlanfactOperations,
  getTasks,
} from '../services/api';
import {
  SortState,
  compareValues,
  formatContractSumDisplay,
  formatDateShort,
  toggleSort,
} from '../utils/table';
import { useAuth } from '../context/AuthContext';
import { ACTIVE_CONTRACT_STATUS } from '../components/ContractPicker';
import { SheetsColumnMultiFilter } from '../components/SheetsColumnMultiFilter';
import { ClosingStatusIcon, closingStatusLabel } from '../components/ClosingStatusIcon';
import { resolveContractFamily, sumFamilyAmount, minFamilyDate, maxFamilyDate } from '../utils/contractFamily';
import { resolveFamilyFinance } from '../utils/contractReceipts';
import {
  resolveFamilyExpenseClosingCheck,
  resolveFamilyStageClosingCheck,
  resolveFamilyStageReferenceDate,
} from '../utils/contractDeadlines';
import { ContractDeadlineInfo, formatDeadlineCompact, formatDeadlineRelativeTo } from '../utils/deadline';
import { formatSignedBalance } from '../utils/taskFinance';
import '../styles/DataPage.css';

function resolveFamilyAllSignedStatus(familyContracts: Contract[]): SmoUlClosingStatus {
  if (familyContracts.length === 0) {
    return 'open';
  }
  const signedCount = familyContracts.filter((contract) => Boolean(contract.has_original)).length;
  if (signedCount === familyContracts.length) {
    return 'closed';
  }
  if (signedCount > 0) {
    return 'partial';
  }
  return 'open';
}

interface FamilyRowMetrics {
  totalAmount: number;
  balance: number;
  paymentRemainder: number;
  deadline: ContractDeadlineInfo;
  startDate: string | null;
  maxEndDate: string | null;
  expenseClosingDate: string | null;
  expensesClosed: SmoUlClosingStatus;
  fullyPaid: SmoUlClosingStatus;
  stagesClosed: SmoUlClosingStatus;
  stagesClosingDate: string | null;
  allSigned: SmoUlClosingStatus;
}

type InProgressContractSortKey =
  | 'contract_number'
  | 'subject'
  | 'contractor'
  | 'manager'
  | 'projectLead'
  | 'start_date'
  | 'end_date'
  | 'overdue'
  | 'balance'
  | 'paymentRemainder'
  | 'total_amount'
  | 'all_signed'
  | 'expenses_closed'
  | 'fully_paid'
  | 'stages_closed';

const CLOSING_STATUS_SORT_RANK: Record<SmoUlClosingStatus, number> = {
  na: 0,
  open: 1,
  partial: 2,
  waiting: 3,
  closed: 4,
};

function resolveFullyPaidStatus(
  totalAmount: number,
  paymentRemainder: number,
  paymentPaid: number,
): SmoUlClosingStatus {
  if (totalAmount > 0 && paymentRemainder <= 0) return 'closed';
  if (paymentPaid > 0) return 'partial';
  return 'open';
}

type InProgressFilterMenuKey = 'number' | 'contractor' | 'manager' | 'project_lead' | null;

const InProgressContractsPage: React.FC = () => {
  const navigate = useNavigate();
  const { can, hasDepartment } = useAuth();
  const [contracts, setContracts] = useState<Contract[]>([]);
  const [contractors, setContractors] = useState<Contractor[]>([]);
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [tasks, setTasks] = useState<Task[]>([]);
  const [workStages, setWorkStages] = useState<ContractStage[]>([]);
  const [planfactOperations, setPlanfactOperations] = useState<PlanfactOperation[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');

  const [filtersOpen, setFiltersOpen] = useState(false);
  const [filterMenuOpen, setFilterMenuOpen] = useState<InProgressFilterMenuKey>(null);

  const [searchText, setSearchText] = useState('');
  const [filterContractNumbers, setFilterContractNumbers] = useState<string[]>([]);
  const [filterContractorIds, setFilterContractorIds] = useState<string[]>([]);
  const [filterManagerIds, setFilterManagerIds] = useState<string[]>([]);
  const [filterLeadIds, setFilterLeadIds] = useState<string[]>([]);
  const [minAmountFilter, setMinAmountFilter] = useState('');
  const [maxAmountFilter, setMaxAmountFilter] = useState('');

  const [sortState, setSortState] = useState<SortState<InProgressContractSortKey>>({
    key: 'total_amount',
    direction: 'desc',
  });

  const canOpenPage =
    hasDepartment('OP', 'OUP_UL', 'ALL_COMP') && can('OUTCOME', 'VIEW');

  const fetchData = useCallback(async (): Promise<void> => {
    if (!canOpenPage) {
      setIsLoading(false);
      setContracts([]);
      return;
    }

    setIsLoading(true);
    try {
      const [contractsData, contractorsData, employeesData, tasksData, planfactData] = await Promise.all([
        getContracts(),
        getContractors(),
        getEmployees(),
        getTasks(),
        getPlanfactOperations().catch(() => [] as PlanfactOperation[]),
      ]);
      const activeFamilyIds = new Set<number>();
      for (const contract of contractsData) {
        if ((contract.hierarchy_level ?? 1) !== 1 || contract.contract_status !== ACTIVE_CONTRACT_STATUS) {
          continue;
        }
        const family = resolveContractFamily(contract, contractsData);
        family.familyIds.forEach((id) => activeFamilyIds.add(id));
      }
      const stageLists = await Promise.all(
        [...activeFamilyIds].map((id) => getContractStages(id).catch(() => [] as ContractStage[])),
      );
      setContracts(contractsData);
      setContractors(contractorsData);
      setEmployees(employeesData);
      setTasks(tasksData);
      setPlanfactOperations(planfactData);
      setWorkStages(stageLists.flat());
    } catch (err) {
      setError('Ошибка при загрузке данных');
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  }, [canOpenPage]);

  useEffect(() => {
    if (canOpenPage) {
      void fetchData();
    } else {
      setIsLoading(false);
      setContracts([]);
    }
  }, [canOpenPage, fetchData]);

  const activeMainContracts = useMemo(
    () =>
      contracts.filter(
        (contract) =>
          (contract.hierarchy_level ?? 1) === 1 && contract.contract_status === ACTIVE_CONTRACT_STATUS,
      ),
    [contracts],
  );

  const familyMetricsByMainId = useMemo(() => {
    const map = new Map<number, FamilyRowMetrics>();

    for (const contract of activeMainContracts) {
      const family = resolveContractFamily(contract, contracts);
      const mainId = family.mainContract.id;

      const familyTasks = tasks
        .filter((task) => family.familyIds.has(task.contract_id))
        .sort((a, b) => a.task_id.localeCompare(b.task_id, 'ru'));

      const portfolio = sumFamilyAmount(family.familyContracts);
      const finance = resolveFamilyFinance({
        familyContractIds: family.familyIds,
        familyTasks,
        mainContract: family.mainContract,
        planfactOperations,
      });
      const startDate = minFamilyDate(family.familyContracts, 'start_date');
      const maxEndDate = maxFamilyDate(family.familyContracts, 'end_date');
      const familyStages = workStages.filter((stage) => family.familyIds.has(stage.contract_id));
      const stageReferenceDate = resolveFamilyStageReferenceDate(familyStages);
      const expenseClosing = resolveFamilyExpenseClosingCheck(familyTasks);
      const stageClosing = resolveFamilyStageClosingCheck(familyStages);
      const deadline = formatDeadlineRelativeTo(maxEndDate, stageReferenceDate);

      map.set(mainId, {
        totalAmount: portfolio,
        balance: finance.balance,
        paymentRemainder: portfolio - finance.paymentPaid,
        deadline,
        startDate,
        maxEndDate,
        expenseClosingDate: expenseClosing.closingDate,
        expensesClosed: expenseClosing.status,
        fullyPaid: resolveFullyPaidStatus(portfolio, portfolio - finance.paymentPaid, finance.paymentPaid),
        stagesClosed: stageClosing.status,
        stagesClosingDate: stageClosing.closingDate,
        allSigned: resolveFamilyAllSignedStatus(family.familyContracts),
      });
    }

    return map;
  }, [activeMainContracts, contracts, tasks, workStages, planfactOperations]);

  const getContractorName = useCallback(
    (id: number): string => contractors.find((c) => c.id === id)?.name || 'Неизвестно',
    [contractors],
  );

  const getEmployeeName = useCallback(
    (id?: number): string => {
      if (!id) {
        return '-';
      }
      return employees.find((employee) => employee.id === id)?.full_name || 'Неизвестно';
    },
    [employees],
  );

  const getFamilyMetrics = useCallback(
    (mainContractId: number): FamilyRowMetrics | null => familyMetricsByMainId.get(mainContractId) ?? null,
    [familyMetricsByMainId],
  );

  const clearFilters = (): void => {
    setSearchText('');
    setFilterContractNumbers([]);
    setFilterContractorIds([]);
    setFilterManagerIds([]);
    setFilterLeadIds([]);
    setMinAmountFilter('');
    setMaxAmountFilter('');
    setFilterMenuOpen(null);
  };

  const hasActiveFilters =
    filterContractNumbers.length > 0 ||
    filterContractorIds.length > 0 ||
    filterManagerIds.length > 0 ||
    filterLeadIds.length > 0 ||
    minAmountFilter.trim() !== '' ||
    maxAmountFilter.trim() !== '';

  useEffect(() => {
    if (!filtersOpen) {
      setFilterMenuOpen(null);
    }
  }, [filtersOpen]);

  const contractNumberFilterOptions = useMemo(() => {
    const set = new Set(activeMainContracts.map((c) => c.contract_number));
    return [...set].sort((a, b) => a.localeCompare(b, 'ru', { numeric: true }));
  }, [activeMainContracts]);

  const contractorFilterOptions = useMemo(() => {
    const set = new Set(activeMainContracts.map((c) => String(c.contractor_id)));
    return [...set].sort((a, b) => Number(a) - Number(b));
  }, [activeMainContracts]);

  const managerFilterOptions = useMemo(() => {
    const set = new Set<string>();
    for (const c of activeMainContracts) {
      if (c.manager_id != null) {
        set.add(String(c.manager_id));
      }
    }
    return [...set].sort((a, b) => getEmployeeName(Number(a)).localeCompare(getEmployeeName(Number(b)), 'ru'));
  }, [activeMainContracts, getEmployeeName]);

  const projectLeadFilterOptions = useMemo(() => {
    const set = new Set<string>();
    for (const c of activeMainContracts) {
      if (c.project_lead_id != null) {
        set.add(String(c.project_lead_id));
      }
    }
    return [...set].sort((a, b) => getEmployeeName(Number(a)).localeCompare(getEmployeeName(Number(b)), 'ru'));
  }, [activeMainContracts, getEmployeeName]);

  const filteredContracts = useMemo(() => {
    const text = searchText.trim().toLowerCase();

    return activeMainContracts.filter((contract) => {
      const contractorName = getContractorName(contract.contractor_id);
      const managerName = getEmployeeName(contract.manager_id);
      const leadName = getEmployeeName(contract.project_lead_id);
      const metrics = getFamilyMetrics(contract.id);

      const searchable = [
        contract.contract_number,
        contractorName,
        managerName,
        leadName,
        contract.contract_subject,
        metrics?.startDate ? formatDateShort(metrics.startDate) : '',
        metrics?.maxEndDate ? formatDateShort(metrics.maxEndDate) : '',
        metrics ? formatSignedBalance(metrics.balance) : '',
        metrics ? formatContractSumDisplay(metrics.paymentRemainder) : '',
        metrics ? formatContractSumDisplay(metrics.totalAmount) : '',
        metrics?.deadline.label ? metrics.deadline.label.toLowerCase() : '',
        metrics ? formatDeadlineCompact(metrics.deadline) : '',
        metrics ? closingStatusLabel(metrics.allSigned) : '',
        metrics ? closingStatusLabel(metrics.expensesClosed) : '',
        metrics ? closingStatusLabel(metrics.fullyPaid) : '',
        metrics ? closingStatusLabel(metrics.stagesClosed) : '',
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();

      const amount = metrics?.totalAmount ?? (Number(contract.total_amount) || 0);
      const minAmount = minAmountFilter ? Number(minAmountFilter) : Number.NEGATIVE_INFINITY;
      const maxAmount = maxAmountFilter ? Number(maxAmountFilter) : Number.POSITIVE_INFINITY;

      const matchesSearch = !text || searchable.includes(text);
      const matchesNumber =
        filterContractNumbers.length === 0 || filterContractNumbers.includes(contract.contract_number);
      const matchesContractor =
        filterContractorIds.length === 0 || filterContractorIds.includes(String(contract.contractor_id));
      const matchesManager =
        filterManagerIds.length === 0 || filterManagerIds.includes(String(contract.manager_id || ''));
      const matchesLead =
        filterLeadIds.length === 0 || filterLeadIds.includes(String(contract.project_lead_id || ''));
      const matchesAmount = amount >= minAmount && amount <= maxAmount;

      return (
        matchesSearch &&
        matchesNumber &&
        matchesContractor &&
        matchesManager &&
        matchesLead &&
        matchesAmount
      );
    });
  }, [
    activeMainContracts,
    searchText,
    filterContractNumbers,
    filterContractorIds,
    filterManagerIds,
    filterLeadIds,
    minAmountFilter,
    maxAmountFilter,
    getContractorName,
    getEmployeeName,
    getFamilyMetrics,
  ]);

  const sortedContracts = useMemo(() => {
    const getContractSortValue = (
      contract: Contract,
      key: InProgressContractSortKey,
    ): string | number | undefined => {
      const metrics = getFamilyMetrics(contract.id);
      const values: Record<InProgressContractSortKey, string | number | undefined> = {
        contract_number: contract.contract_number,
        subject: contract.contract_subject,
        contractor: getContractorName(contract.contractor_id),
        manager: getEmployeeName(contract.manager_id),
        projectLead: getEmployeeName(contract.project_lead_id),
        start_date: metrics?.startDate ?? undefined,
        end_date: metrics?.maxEndDate ?? undefined,
        overdue: metrics?.maxEndDate ?? undefined,
        balance: metrics?.balance,
        paymentRemainder: metrics?.paymentRemainder,
        total_amount: metrics?.totalAmount,
        all_signed: metrics ? CLOSING_STATUS_SORT_RANK[metrics.allSigned] : undefined,
        expenses_closed: metrics ? CLOSING_STATUS_SORT_RANK[metrics.expensesClosed] : undefined,
        fully_paid: metrics ? CLOSING_STATUS_SORT_RANK[metrics.fullyPaid] : undefined,
        stages_closed: metrics ? CLOSING_STATUS_SORT_RANK[metrics.stagesClosed] : undefined,
      };
      return values[key];
    };

    const { key: sortKey, direction: sortDirection } = sortState;

    return [...filteredContracts].sort((left, right) =>
      compareValues(
        getContractSortValue(left, sortKey),
        getContractSortValue(right, sortKey),
        sortDirection,
      ),
    );
  }, [filteredContracts, sortState, getContractorName, getEmployeeName, getFamilyMetrics]);

  const footerTotals = useMemo(() => {
    let totalAmount = 0;
    let balance = 0;
    let paymentRemainder = 0;
    for (const contract of sortedContracts) {
      const metrics = getFamilyMetrics(contract.id);
      totalAmount += metrics?.totalAmount ?? (Number(contract.total_amount) || 0);
      balance += metrics?.balance ?? 0;
      paymentRemainder += metrics?.paymentRemainder ?? 0;
    }
    return { totalAmount, balance, paymentRemainder };
  }, [sortedContracts, getFamilyMetrics]);

  const handleSort = (key: InProgressContractSortKey): void => {
    setSortState((current) => toggleSort(current, key));
  };

  const tableColSpan = 15;
  const colgroup = (
    <colgroup>
      <col className="contracts-page__colgrid contracts-page__colgrid--contractor" />
      <col className="contracts-page__colgrid contracts-page__colgrid--number" />
      <col className="contracts-page__colgrid contracts-page__colgrid--subject" />
      <col className="contracts-page__colgrid contracts-page__colgrid--person" />
      <col className="contracts-page__colgrid contracts-page__colgrid--person" />
      <col className="contracts-page__colgrid contracts-page__colgrid--date" />
      <col className="contracts-page__colgrid contracts-page__colgrid--date" />
      <col className="contracts-page__colgrid in-progress-contracts-page__colgrid--overdue" />
      <col className="contracts-page__colgrid contracts-page__colgrid--sum" />
      <col className="contracts-page__colgrid in-progress-contracts-page__colgrid--finance" />
      <col className="contracts-page__colgrid in-progress-contracts-page__colgrid--finance" />
      <col className="contracts-page__colgrid in-progress-contracts-page__colgrid--check" />
      <col className="contracts-page__colgrid in-progress-contracts-page__colgrid--check" />
      <col className="contracts-page__colgrid in-progress-contracts-page__colgrid--check" />
      <col className="contracts-page__colgrid in-progress-contracts-page__colgrid--check" />
    </colgroup>
  );

  return (
    <div className="data-page contracts-page in-progress-contracts-page in-progress-smo-ul-page">
      {!canOpenPage ? (
        <div className="empty-state">
          Нет доступа к странице (нужны отдел OP / OUP_UL / ALL_COMP и OUTCOME VIEW).
        </div>
      ) : (
        <>
          <div className="page-header-row">
            <h1>Это твои активные договоры</h1>
            <div className="page-header-row__actions">
              <button
                type="button"
                className={`filters-toggle ${!filtersOpen && hasActiveFilters ? 'filters-toggle--show-active-dot' : ''}`}
                onClick={() => setFiltersOpen((open) => !open)}
                aria-expanded={filtersOpen}
                aria-label={filtersOpen ? 'Скрыть фильтры' : 'Показать фильтры'}
              >
                Фильтры
                <span className="filters-toggle-badge" aria-hidden />
              </button>
            </div>
            <div className="page-header-row__search">
              <input
                type="text"
                className="search-input"
                placeholder="Поиск по текстовым полям"
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
              />
            </div>
          </div>

          {error && <div className="error-message">{error}</div>}

          <div className={`table-layout ${filtersOpen ? '' : 'table-layout--filters-collapsed'}`}>
            {filtersOpen && (
              <aside className="filters-panel">
                <div className="filters-header">
                  <h2>Фильтры</h2>
                  <button type="button" className="link-button" onClick={clearFilters}>
                    Сбросить
                  </button>
                </div>
                <div className="filters-grid">
                  <div className="filters-field">
                    <SheetsColumnMultiFilter
                      label="Номер договора"
                      options={contractNumberFilterOptions}
                      selected={filterContractNumbers}
                      onSelectedChange={setFilterContractNumbers}
                      formatOption={(v) => v}
                      isOpen={filterMenuOpen === 'number'}
                      onOpenChange={(open) => setFilterMenuOpen(open ? 'number' : null)}
                    />
                  </div>
                  <div className="filters-field">
                    <SheetsColumnMultiFilter
                      label="Контрагент"
                      options={contractorFilterOptions}
                      selected={filterContractorIds}
                      onSelectedChange={setFilterContractorIds}
                      formatOption={(v) => getContractorName(Number(v))}
                      isOpen={filterMenuOpen === 'contractor'}
                      onOpenChange={(open) => setFilterMenuOpen(open ? 'contractor' : null)}
                    />
                  </div>
                  <div className="filters-field">
                    <SheetsColumnMultiFilter
                      label="Менеджер"
                      options={managerFilterOptions}
                      selected={filterManagerIds}
                      onSelectedChange={setFilterManagerIds}
                      formatOption={(v) => getEmployeeName(Number(v))}
                      isOpen={filterMenuOpen === 'manager'}
                      onOpenChange={(open) => setFilterMenuOpen(open ? 'manager' : null)}
                    />
                  </div>
                  <div className="filters-field">
                    <SheetsColumnMultiFilter
                      label="РП"
                      options={projectLeadFilterOptions}
                      selected={filterLeadIds}
                      onSelectedChange={setFilterLeadIds}
                      formatOption={(v) => getEmployeeName(Number(v))}
                      isOpen={filterMenuOpen === 'project_lead'}
                      onOpenChange={(open) => setFilterMenuOpen(open ? 'project_lead' : null)}
                    />
                  </div>
                  <label className="filters-sum-block">
                    <span className="filters-sum-block__title">Сумма</span>
                    <div className="filters-sum-block__row">
                      <input
                        type="text"
                        inputMode="decimal"
                        className="filters-sum-block__input"
                        placeholder="от"
                        value={minAmountFilter}
                        onChange={(e) => setMinAmountFilter(e.target.value)}
                      />
                      <input
                        type="text"
                        inputMode="decimal"
                        className="filters-sum-block__input"
                        placeholder="до"
                        value={maxAmountFilter}
                        onChange={(e) => setMaxAmountFilter(e.target.value)}
                      />
                    </div>
                  </label>
                </div>
              </aside>
            )}

            <section className="table-content">
              {isLoading ? (
                <div className="loading">Загрузка...</div>
              ) : (
                <div className="table-panel">
                  <div className="table-scroll">
                    <table className="data-table contracts-page__table in-progress-contracts-page__table">
                      {colgroup}
                      <thead>
                        <tr>
                          <th className="sortable contracts-page__th-contractor" onClick={() => handleSort('contractor')} role="button">Контрагент</th>
                          <th className="sortable contracts-page__th-number" onClick={() => handleSort('contract_number')} role="button">Номер договора</th>
                          <th className="sortable contracts-page__th-subject" onClick={() => handleSort('subject')} role="button">Предмет договора</th>
                          <th className="sortable contracts-page__th-person" onClick={() => handleSort('manager')} role="button">Менеджер</th>
                          <th className="sortable contracts-page__th-person" onClick={() => handleSort('projectLead')} role="button">РП</th>
                          <th className="sortable contracts-page__th-date" onClick={() => handleSort('start_date')} role="button">Начало</th>
                          <th className="sortable contracts-page__th-date" onClick={() => handleSort('end_date')} role="button">Срок</th>
                          <th className="sortable in-progress-contracts-page__th-overdue" onClick={() => handleSort('overdue')} role="button">Просрочка</th>
                          <th className="sortable col-num contracts-page__th-sum" onClick={() => handleSort('total_amount')} role="button">Сумма</th>
                          <th className="sortable col-num in-progress-contracts-page__th-payment-remainder" onClick={() => handleSort('paymentRemainder')} role="button">Остаток по оплатам</th>
                          <th className="sortable col-num in-progress-contracts-page__th-balance" onClick={() => handleSort('balance')} role="button">Денежный Поток</th>
                          <th
                            className="sortable in-progress-smo-ul-page__th-closing in-progress-contracts-page__th-check"
                            onClick={() => handleSort('all_signed')}
                            role="button"
                          >
                            Все договоры подписаны
                          </th>
                          <th
                            className="sortable in-progress-smo-ul-page__th-closing in-progress-contracts-page__th-check"
                            onClick={() => handleSort('expenses_closed')}
                            role="button"
                          >
                            Все задачи закрыты
                          </th>
                          <th
                            className="sortable in-progress-smo-ul-page__th-closing in-progress-contracts-page__th-check"
                            onClick={() => handleSort('fully_paid')}
                            role="button"
                          >
                            Полная оплата
                          </th>
                          <th
                            className="sortable in-progress-smo-ul-page__th-closing in-progress-contracts-page__th-check"
                            onClick={() => handleSort('stages_closed')}
                            role="button"
                          >
                            Все этапы закрыты
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {sortedContracts.length === 0 ? (
                          <tr>
                            <td colSpan={tableColSpan} className="data-table__empty">
                              Ничего не найдено
                            </td>
                          </tr>
                        ) : (
                          sortedContracts.map((contract) => {
                            const metrics = getFamilyMetrics(contract.id);

                            return (
                              <tr
                                key={contract.id}
                                className="clickable-row contracts-page__row--main"
                                onClick={(e) => {
                                  if (e.ctrlKey || e.metaKey) {
                                    return;
                                  }
                                  navigate(`/contracts/${contract.id}`);
                                }}
                              >
                                <td className="contracts-page__td-contractor">
                                  <div className="contracts-page__clamp-2">{getContractorName(contract.contractor_id)}</div>
                                </td>
                                <td className="contracts-page__td-number">
                                  <div className="contracts-page__clamp-2">
                                    <strong>{contract.contract_number}</strong>
                                  </div>
                                </td>
                                <td className="contracts-page__td-subject">
                                  <div className="contracts-page__clamp-2">{contract.contract_subject || '—'}</div>
                                </td>
                                <td className="contracts-page__td-person">
                                  <div className="contracts-page__clamp-2">{getEmployeeName(contract.manager_id)}</div>
                                </td>
                                <td className="contracts-page__td-person">
                                  <div className="contracts-page__clamp-2">{getEmployeeName(contract.project_lead_id)}</div>
                                </td>
                                <td className="contracts-page__td-date">
                                  {metrics?.startDate ? formatDateShort(metrics.startDate) : '—'}
                                </td>
                                <td className="contracts-page__td-date">
                                  {metrics?.maxEndDate ? formatDateShort(metrics.maxEndDate) : '—'}
                                </td>
                                <td className={`in-progress-contracts-page__td-overdue${metrics?.deadline.overdue ? ' in-progress-contracts-page__cell--overdue' : ''}`}>
                                  {metrics?.deadline.label ? (
                                    <div className="contracts-page__clamp-2 in-progress-contracts-page__deadline">
                                      <span className="in-progress-contracts-page__deadline-label">
                                        {metrics.deadline.label.toLowerCase()}
                                      </span>
                                      <span>{formatDeadlineCompact(metrics.deadline)}</span>
                                    </div>
                                  ) : (
                                    '—'
                                  )}
                                </td>
                                <td className="col-num contracts-page__td-sum">
                                  <strong>{metrics ? formatContractSumDisplay(metrics.totalAmount) : '—'}</strong>
                                </td>
                                <td className="col-num in-progress-contracts-page__td-payment-remainder">
                                  {metrics ? formatContractSumDisplay(metrics.paymentRemainder) : '—'}
                                </td>
                                <td className={`col-num in-progress-contracts-page__td-balance${metrics && metrics.balance < 0 ? ' in-progress-contracts-page__cell--negative' : ''}`}>
                                  {metrics ? formatSignedBalance(metrics.balance) : '—'}
                                </td>
                                <td className="in-progress-smo-ul-page__td-closing in-progress-contracts-page__td-check">
                                  <ClosingStatusIcon status={metrics?.allSigned ?? 'open'} />
                                </td>
                                <td className="in-progress-smo-ul-page__td-closing in-progress-contracts-page__td-check">
                                  <ClosingStatusIcon
                                    status={metrics?.expensesClosed ?? 'open'}
                                    closingDate={metrics?.expenseClosingDate}
                                  />
                                </td>
                                <td className="in-progress-smo-ul-page__td-closing in-progress-contracts-page__td-check">
                                  <ClosingStatusIcon status={metrics?.fullyPaid ?? 'open'} />
                                </td>
                                <td className="in-progress-smo-ul-page__td-closing in-progress-contracts-page__td-check">
                                  <ClosingStatusIcon
                                    status={metrics?.stagesClosed ?? 'open'}
                                    closingDate={metrics?.stagesClosingDate}
                                  />
                                </td>
                              </tr>
                            );
                          })
                        )}
                      </tbody>
                    </table>
                  </div>
                  <div className="table-panel__footer">
                    <table className="data-table data-table--footer contracts-page__table in-progress-contracts-page__table">
                      {colgroup}
                      <tfoot>
                        <tr>
                          <td>Итого: {sortedContracts.length}</td>
                          <td />
                          <td />
                          <td />
                          <td />
                          <td />
                          <td />
                          <td />
                          <td className="col-num"><strong>{formatContractSumDisplay(footerTotals.totalAmount)}</strong></td>
                          <td className="col-num">{formatContractSumDisplay(footerTotals.paymentRemainder)}</td>
                          <td className={`col-num${footerTotals.balance < 0 ? ' in-progress-contracts-page__cell--negative' : ''}`}>
                            {formatSignedBalance(footerTotals.balance)}
                          </td>
                          <td className="in-progress-smo-ul-page__td-closing" />
                          <td className="in-progress-smo-ul-page__td-closing" />
                          <td className="in-progress-smo-ul-page__td-closing" />
                          <td className="in-progress-smo-ul-page__td-closing" />
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                </div>
              )}
            </section>
          </div>
        </>
      )}
    </div>
  );
};

export default InProgressContractsPage;
